import { Helmet } from 'react-helmet-async';
import { useI18n } from '../../contexts/I18nContext';
import { LOCALES, DEFAULT_LOCALE, SITE_URL } from '../../config/locales';

interface SEOVideo {
  url: string;
  poster?: string;
  width?: number;
  height?: number;
  type?: string;
}

interface SEOProps {
  title: string;
  description: string;
  type?: string;
  name?: string;
  url?: string;
  image?: string;
  // Path senza lingua (es. '' per la landing, '/video/mouse'): se presente genera
  // canonical + matrice hreflang completa per tutte le 36 locale.
  path?: string;
  // Uno o piu' oggetti JSON-LD (schema.org) renderizzati come <script type="application/ld+json">.
  jsonLd?: Record<string, unknown> | Array<Record<string, unknown>>;
  // Video Open Graph per anteprime social ricche (MP4 diretto).
  video?: SEOVideo;
  noindex?: boolean;
}

export function SEO({
  title,
  description,
  type = 'website',
  name = 'Jarvis Interviews',
  url = SITE_URL,
  image = `${SITE_URL}/og-image.png`,
  path,
  jsonLd,
  video,
  noindex = false,
}: SEOProps) {
  const { lang } = useI18n();

  const canonical = path !== undefined ? `${SITE_URL}/${lang}${path}` : url;
  const videoImage = video?.poster ?? image;
  const jsonLdItems = jsonLd ? (Array.isArray(jsonLd) ? jsonLd : [jsonLd]) : [];

  return (
    <Helmet htmlAttributes={{ lang }}>
      {/* Standard metadata tags */}
      <title>{title}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content="AI copilot per consulenza, AI sales copilot italiano, real time meeting assistant AI, live tech interview copilot B2B, assistente AI in tempo reale per call, software gestione obiezioni vendite AI" />
      {noindex && <meta name="robots" content="noindex, follow" />}
      <link rel="canonical" href={canonical} />

      {/* Hreflang per SEO internazionale: matrice completa (36 lingue) quando il path e' noto. */}
      {path !== undefined ? (
        <>
          {LOCALES.map((l) => (
            <link key={l} rel="alternate" hrefLang={l} href={`${SITE_URL}/${l}${path}`} />
          ))}
          <link rel="alternate" hrefLang="x-default" href={`${SITE_URL}/${DEFAULT_LOCALE}${path}`} />
        </>
      ) : (
        <>
          <link rel="alternate" hrefLang="it" href={`${url}`} />
          <link rel="alternate" hrefLang="en" href={`${url}?lang=en`} />
          <link rel="alternate" hrefLang="x-default" href={`${url}`} />
        </>
      )}

      {/* Open Graph tags for Facebook, LinkedIn, etc. */}
      <meta property="og:type" content={video ? 'video.other' : type} />
      <meta property="og:title" content={title} />
      <meta property="og:description" content={description} />
      <meta property="og:site_name" content={name} />
      <meta property="og:url" content={canonical} />
      <meta property="og:image" content={videoImage} />
      <meta property="og:locale" content={lang} />

      {video && (
        <>
          <meta property="og:video" content={video.url} />
          <meta property="og:video:secure_url" content={video.url} />
          <meta property="og:video:type" content={video.type ?? 'video/mp4'} />
          {video.width && <meta property="og:video:width" content={String(video.width)} />}
          {video.height && <meta property="og:video:height" content={String(video.height)} />}
        </>
      )}

      {/* Twitter tags (summary_large_image con poster: X non valida player con MP4 diretto) */}
      <meta name="twitter:creator" content="@JarvisInterview" />
      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={title} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={videoImage} />

      {/* Structured data (schema.org) */}
      {jsonLdItems.map((item, i) => (
        <script
          key={i}
          type="application/ld+json"
          dangerouslySetInnerHTML={{ __html: JSON.stringify(item).replace(/</g, '\\u003c') }}
        />
      ))}
    </Helmet>
  );
}
